import React, { useState, useRef } from "react";
import axios from "axios";
import { backendDomain } from "../../../utils";
import "./ImageAgent.css";

const ImageAgent = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [todoText, setTodoText] = useState("");
  const [loading, setLoading] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const [error, setError] = useState(null);
  const [processingTime, setProcessingTime] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);
  const fileInputRef = useRef(null);

  // Pre-loaded images from the public folder
  const preloadedImages = [
    "0a1bcc2f4616ebc1fdb259109a031330.jpg",
    "0a662eb17ba5b22e520e1227821e9ccd.jpg",
    "1.jpg",
    "1a5d4970efc8298776ce7b7bfae2ced7.jpg",
    "20277e2f05123aa6a72c962e64386e7c.jpg",
    "421621a60acc4bd5698beecb930d62ae.jpg",
    "84ea0b38b022fe851bde8fc7b55cae81.jpg",
    "95a3f9ef5bf7f3326d618b6233a3eda5.jpg",
    "9a790650bcce1680436fa26395b7c545.jpg",
    "a6826c257bb2200b5ac46895d5e48af2.jpg",
    "af6ee96555d7833fb8d403e66190e307.jpg",
    "c93ce3a927010b51abd76078a417a587.jpg",
    "ce833fa842637b754641ae7110dd0b8b.jpg",
    "e672026f91de6967f9a5e0e923d07d39.jpg",
    "fb5bba46eddb20f11919278dd8f19836.jpg",
  ];

  const handleImageSelect = (imageName) => {
    setSelectedImage(imageName);
    setUploadedImage(null);
    setResultImage(null);
    setError(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result);
        setSelectedImage(null);
        setResultImage(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGenerate = async () => {
    if (!selectedImage && !uploadedImage) {
      setError("Please select or upload an image");
      return;
    }

    if (!todoText.trim()) {
      setError("Please enter your to-do text");
      return;
    }

    setLoading(true);
    setError(null);
    setResultImage(null);
    setProcessingTime(null);
    setTokenUsage(null);

    const startTime = Date.now();

    try {
      let imageData = null;

      if (uploadedImage) {
        // Use uploaded image
        imageData = uploadedImage;
      } else if (selectedImage) {
        // Fetch preloaded image and convert to base64
        const response = await fetch(`/image-agent-images/${selectedImage}`);
        const blob = await response.blob();
        const reader = new FileReader();
        imageData = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }

      const response = await axios.post(
        `${backendDomain()}/api/image-agent/replace-text`,
        {
          image: imageData,
          replacementText: todoText,
        }
      );

      if (response.data.success && response.data.imageUrl) {
        setResultImage(response.data.imageUrl);
        // Use server-side processing time if available, otherwise calculate client-side
        const time = response.data.processingTime || (Date.now() - startTime);
        setProcessingTime(time);
        if (response.data.tokenUsage) {
          setTokenUsage(response.data.tokenUsage);
        }
      } else {
        setError(response.data.error || "Failed to generate image");
        if (response.data.processingTime) {
          setProcessingTime(response.data.processingTime);
        }
      }
    } catch (err) {
      console.error("Error generating image:", err);
      setError(
        err.response?.data?.error ||
          "An error occurred while processing your image"
      );
      if (err.response?.data?.processingTime) {
        setProcessingTime(err.response.data.processingTime);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedImage(null);
    setUploadedImage(null);
    setTodoText("");
    setResultImage(null);
    setError(null);
    setProcessingTime(null);
    setTokenUsage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="image-agent">
      <div className="header">
        <div className="header-content">
          <div className="title-section">
            <h1>Neuromantics - Image Agent</h1>
            <p className="subtitle">
              Replace text in images with your custom to-do list
            </p>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="input-section">
          <div className="section-card">
            <h2>1. Select or Upload Image</h2>
            
            <div className="upload-section">
              <button
                className="upload-button"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Your Image
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
            </div>

            {uploadedImage && (
              <div className="preview-image">
                <img src={uploadedImage} alt="Uploaded" />
                <button
                  className="remove-button"
                  onClick={() => {
                    setUploadedImage(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = "";
                    }
                  }}
                >
                  Remove
                </button>
              </div>
            )}

            <div className="preloaded-images-section">
              <h3>Or select from pre-loaded images:</h3>
              <div className="image-grid">
                {preloadedImages.map((imageName) => (
                  <div
                    key={imageName}
                    className={`image-thumbnail ${
                      selectedImage === imageName ? "selected" : ""
                    }`}
                    onClick={() => handleImageSelect(imageName)}
                  >
                    <img
                      src={`/image-agent-images/${imageName}`}
                      alt={imageName}
                    />
                  </div>
                ))}
              </div>
            </div>

            {selectedImage && !uploadedImage && (
              <div className="selected-image-preview">
                <h3>Selected Image:</h3>
                <img
                  src={`/image-agent-images/${selectedImage}`}
                  alt="Selected"
                />
              </div>
            )}
          </div>

          <div className="section-card">
            <h2>2. Enter Your To-Do Text</h2>
            <textarea
              className="todo-textarea"
              placeholder="Enter your to-do list items here..."
              value={todoText}
              onChange={(e) => setTodoText(e.target.value)}
              rows={6}
            />
          </div>

          <div className="section-card">
            <div className="action-buttons">
              <button
                className="generate-button"
                onClick={handleGenerate}
                disabled={
                  loading ||
                  (!selectedImage && !uploadedImage) ||
                  !todoText.trim()
                }
              >
                {loading ? "Processing..." : "Generate Image"}
              </button>
              <button className="reset-button" onClick={handleReset}>
                Reset
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}
          </div>
        </div>

        <div className="result-section">
          <div className="section-card">
            <h2>3. Result</h2>
            {loading && (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Processing your image with AI...</p>
              </div>
            )}
            {resultImage && (
              <div className="result-image-container">
                <img src={resultImage} alt="Generated result" />
                <a
                  href={resultImage}
                  download="generated-image.png"
                  className="download-button"
                >
                  Download Image
                </a>
              </div>
            )}
            {!loading && !resultImage && (
              <div className="placeholder">
                <p>Your generated image will appear here</p>
              </div>
            )}
            {(processingTime || tokenUsage) && (
              <div className="metrics-card">
                <h3>Processing Metrics</h3>
                {processingTime && (
                  <div className="metric-item">
                    <span className="metric-label">Processing Time:</span>
                    <span className="metric-value">
                      {(processingTime / 1000).toFixed(2)}s
                    </span>
                  </div>
                )}
                {tokenUsage && (
                  <>
                    <div className="token-metrics">
                      <div className="metric-item">
                        <span className="metric-label">Prompt Tokens:</span>
                        <span className="metric-value">
                          {tokenUsage.promptTokenCount.toLocaleString()}
                        </span>
                      </div>
                      <div className="metric-item">
                        <span className="metric-label">Response Tokens:</span>
                        <span className="metric-value">
                          {tokenUsage.candidatesTokenCount.toLocaleString()}
                        </span>
                      </div>
                      <div className="metric-item metric-total">
                        <span className="metric-label">Total Tokens:</span>
                        <span className="metric-value">
                          {tokenUsage.totalTokenCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    {(() => {
                      // Pricing based on Gemini 3 Pro Image Preview:
                      // Input: $2.00 per 1M tokens (text/image input)
                      //        Image input: $0.0011 per image (560 tokens)
                      // Output: $120.00 per 1M tokens for image output
                      //         1K/2K images: $0.134 per image (1120 tokens)
                      //         4K images: $0.24 per image (2000 tokens)
                      
                      // Calculate input cost
                      // Since we're sending one image, account for image input cost
                      const imageInputCost = 0.0011; // $0.0011 per image
                      const textInputTokens = Math.max(0, tokenUsage.promptTokenCount - 560);
                      const textInputCost = (textInputTokens / 1_000_000) * 2;
                      const inputCost = imageInputCost + textInputCost;
                      
                      // Calculate output cost based on token count
                      // Determine if it's 1K/2K (~1120 tokens) or 4K (~2000 tokens) or use token-based pricing
                      let outputCost;
                      const outputTokens = tokenUsage.candidatesTokenCount;
                      
                      if (outputTokens >= 1900 && outputTokens <= 2100) {
                        // Likely 4K image
                        outputCost = 0.24;
                      } else if (outputTokens >= 1000 && outputTokens <= 1250) {
                        // Likely 1K/2K image
                        outputCost = 0.134;
                      } else {
                        // Use token-based pricing: $120 per 1M tokens
                        outputCost = (outputTokens / 1_000_000) * 120;
                      }
                      
                      const totalCost = inputCost + outputCost;
                      return (
                        <div className="cost-metrics">
                          <div className="metric-item">
                            <span className="metric-label">Input Cost:</span>
                            <span className="metric-value">
                              ${inputCost.toFixed(6)}
                            </span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-label">Output Cost:</span>
                            <span className="metric-value">
                              ${outputCost.toFixed(6)}
                            </span>
                          </div>
                          <div className="metric-item metric-total cost-total">
                            <span className="metric-label">Total Cost:</span>
                            <span className="metric-value">
                              ${totalCost.toFixed(6)}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageAgent;
